import React from 'react';
import { Brush, LineChart, Line, Label, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea, ComposedChart, ResponsiveContainer } from 'recharts';
import i18next from 'i18next';

import CustomTooltip from './custom_tooltip';
import GraphUtility from './graph_utility';

class LineGraph extends React.Component {
  constructor(props) {
    super(props);

    this.Y_AXIS_WIDTH = 100;
    this.BRUSH_GRAPH_HEIGHT = 100;
    this.GRAPH_X_HEIGHT = 50;
    this.BRUSH_HEIGHT = this.BRUSH_GRAPH_HEIGHT - this.GRAPH_X_HEIGHT;

    this.state = {
      startIndex: 0,
      endIndex: this.props.graphVitals.data.length - 1,
      showDot: this.props.graphVitals.data && this.props.graphVitals.data.filter(d => d.amount).length === 1
    }

    this.renderBrush = this.renderBrush.bind(this);
    this.handleBrushChange = this.handleBrushChange.bind(this);
    this.formatTicks = this.formatTicks.bind(this);
    this.calculateTicks = this.calculateTicks.bind(this);
  }

  formatTicks(tick, isBrush = false) {
    const data = this.props.graphVitals.data;
    const brushStart = data[isBrush ? 0 : this.state.startIndex].timestamp;
    const brushEnd = data[isBrush ? data.length - 1 : this.state.endIndex].timestamp;
    return GraphUtility.formatTicks(tick, brushStart, brushEnd);
  } 

  calculateTicks(args) {
    const data = this.props.graphVitals.data;
    const brushStart = data[!args.brush ? this.state.startIndex : 0].timestamp;
    const brushEnd = data[!args.brush ? this.state.endIndex : data.length - 1].timestamp;
    return GraphUtility.calculateTicks(brushStart, brushEnd);
  } 

  handleBrushChange(args) {
    this.setState(
      {
        startIndex: args.startIndex,
        endIndex: args.endIndex,
      }
    );
  } 

  renderBrush(label) {
    const firstTime = this.props.graphVitals.data[0].timestamp;
    const lastTime = this.props.graphVitals.data[this.props.graphVitals.data.length-1].timestamp;
    const { outages, outagesAtStartOrEnd } = this.props.graphVitals;

    return (
        <div className="chart-brush-container">
          <div>
            <LineChart width={this.props.width} height={this.BRUSH_GRAPH_HEIGHT} data={this.props.graphVitals.data}>
              <CartesianGrid strokeDasharray="0 0"/>
              <XAxis type="number" dataKey="timestamp" scale="time" ticks={this.calculateTicks({brush: true})} domain={[firstTime, lastTime]} tickFormatter={(tick) => this.formatTicks(tick, true)} height={this.GRAPH_X_HEIGHT}>
                <Label value={i18next.t('graphing.date-time')} position="insideBottom"/>
              </XAxis>
              <YAxis type="number" allowDecimals={true} domain={['auto', 'auto']} width={this.Y_AXIS_WIDTH}/>
              <Line type="step" dataKey="amount" fill="rgb(76, 120, 168)" dot={this.state.showDot} strokeWidth={2} isAnimationActive={false}/>
              {outages && outages.length > 0 ?
              outages.map((outage, index) => (
                <ReferenceArea key={index} x1={outage.start} x2={outage.end} stroke="red" strokeOpacity={0.125} />
              ))
              : ""}
              { outagesAtStartOrEnd && outagesAtStartOrEnd.length > 0 ?
                outagesAtStartOrEnd.map((outage, index) => (
                  <ReferenceArea key={index} x1={outage.start > firstTime ? outage.start : firstTime} x2={outage.end < lastTime ? outage.end : lastTime} fill="yellow" fillOpacity={0.125}/>
                ))
              : null} 
            </LineChart>
          </div>
          <div className="chart-brush">
            <ComposedChart width={this.props.width} height={this.BRUSH_HEIGHT} data={this.props.graphVitals.data}>
              <XAxis type="number" dataKey="timestamp" scale="time" domain={[firstTime, lastTime]} height={-60}/>
              <YAxis type="number" domain={['auto', 'auto']} width={this.Y_AXIS_WIDTH}/>
              <Brush height={this.BRUSH_HEIGHT} dataKey='timestamp' startIndex={this.state.startIndex} endIndex={this.state.endIndex} fill="rgba(0, 0, 0, 0)" stroke="gray" onChange={this.handleBrushChange} tickFormatter={() => ""}/>
            </ComposedChart>
          </div>
        </div>
    );
  }

  render() {
    let { startIndex, endIndex } = this.state;
    let { data, outages, outagesAtStartOrEnd } = this.props.graphVitals;
    let chartHeight = this.props.showBrush ? this.props.height - this.BRUSH_GRAPH_HEIGHT : this.props.height;
    const firstTime = data[startIndex].timestamp;
    const lastTime = data[endIndex].timestamp;
    const label = this.props.graphVitals.label;

    if (this.props.showBrush) {
      data = data.slice(startIndex, endIndex + 1);
      outages = outages ? outages.filter(outage => outage.start >= firstTime && outage.end <= lastTime) : null;
    }

    return (
      <ResponsiveContainer width='100%' height={this.props.height}>
        <div style={{marginLeft: -45}}>
          <LineChart width={this.props.width} height={chartHeight} data={data} type="step">
            <CartesianGrid strokeDasharray="0 0"/>
            <Tooltip content={<CustomTooltip graphType="line" external={window.external} />}/>

            <XAxis type="number" dataKey="timestamp" scale="time" domain={[firstTime, lastTime]} ticks={this.calculateTicks({brush: false})} tickFormatter={this.formatTicks} height={this.GRAPH_X_HEIGHT}>
            {!this.props.showBrush ? <Label value={i18next.t('graphing.date-time')} position="insideBottom"/>: null}
            </XAxis>
            <YAxis type="number" allowDecimals={true} domain={['auto', 'auto']} padding={{top: 15, bottom: 15}} width={this.Y_AXIS_WIDTH}>
              {label && <Label value={label} angle={-90} />}
            </YAxis>

            {outages && outages.length > 0 ?
              outages.map((outage, index) => (
                <ReferenceArea key={index} x1={outage.start} x2={outage.end} fill="red" fillOpacity={0.125} />
              ))
            : null }
            { outagesAtStartOrEnd && outagesAtStartOrEnd.length > 0 ?
              outagesAtStartOrEnd.map((outage, index) => (
                <ReferenceArea key={index} x1={outage.start > firstTime ? outage.start : firstTime} x2={outage.end < lastTime ? outage.end : lastTime} fill="yellow" fillOpacity={0.125}/>
              ))
            : null}
            <Line type="step" dot={this.state.showDot} strokeWidth={2} dataKey="amount" name={label} fill="rgb(76, 120, 168)" isAnimationActive={false}/>
          </LineChart>

          {this.props.showBrush ? this.renderBrush(label) : null}
        </div>
      </ResponsiveContainer>
    );
  }
}

export default LineGraph;
